import React, { useState } from "react";
import { View, Text, TextInput, TouchableOpacity, StyleSheet, Alert, ImageBackground } from "react-native";
import { usePrivy, useLoginWithEmail, useLoginWithSms } from "@privy-io/react-auth";

function LoginScreen() {
    const { authenticated, ready } = usePrivy();
    const { sendCode: sendEmailCode, loginWithCode: loginWithEmailCode } = useLoginWithEmail();
    const { sendCode: sendSMSCode, loginWithCode: loginWithSMSCode } = useLoginWithSms();

    const [authMethod, setAuthMethod] = useState("email"); // "phone" or "email"
    const [phoneNumber, setPhoneNumber] = useState("");
    const [email, setEmail] = useState("");
    const [code, setCode] = useState("");
    const [codeSent, setCodeSent] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const handleSendCode = async () => {
        if (authMethod === "phone" && !phoneNumber.trim()) {
            Alert.alert("Error", "Please enter a valid phone number");
            return;
        }
        if (authMethod === "email" && !email.trim()) {
            Alert.alert("Error", "Please enter a valid email address");
            return;
        }

        try {
            setIsLoading(true);
            if (authMethod === "phone") {
                await sendSMSCode({ phoneNumber: phoneNumber.trim() });
                Alert.alert("Success", "SMS code sent to your phone number");
            } else {
                await sendEmailCode({ email: email.trim() });
                Alert.alert("Success", "Verification code sent to your email");
            }
            setCodeSent(true);
        } catch (error) {
            console.error("Error sending code:", error);
            const method = authMethod === "phone" ? "SMS" : "email";
            Alert.alert("Error", `Failed to send ${method} code. Please try again.`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleLogin = async () => {
        if (!code.trim()) {
            Alert.alert("Error", "Please enter the verification code");
            return;
        }

        try {
            setIsLoading(true);
            if (authMethod === "phone") {
                await loginWithSMSCode({ code: code.trim() });
            } else {
                await loginWithEmailCode({ code: code.trim() });
            }
            Alert.alert("Success", "Logged in successfully!");
        } catch (error) {
            console.error("Error logging in:", error);
            Alert.alert("Error", "Invalid verification code. Please try again.");
            setCode("");
        } finally {
            setIsLoading(false);
        }
    };

    if (!ready) {
        return (
            <View style={styles.container}>
                <Text style={styles.loadingText}>Loading...</Text>
            </View>
        );
    }

    return (
        <ImageBackground
            source={require("../assets/bg.jpg")}
            style={styles.container}
            imageStyle={{ opacity: 0.3 }}
        >
            <View style={styles.overlay}>
                <View style={styles.content}>
                    {/* Logo/Title */}
                    <View style={styles.titleContainer}>
                        <Text style={styles.title}>Disgo</Text>
                        <Text style={styles.subtitle}>Welcome back</Text>
                    </View>

                    {/* Auth Method Toggle */}
                    <View style={styles.toggleContainer}>
                        <TouchableOpacity
                            style={[styles.toggleButton, authMethod === "email" && styles.toggleButtonActive]}
                            onPress={() => {
                                setAuthMethod("email");
                                setCodeSent(false);
                                setCode("");
                            }}
                        >
                            <Text style={[styles.toggleText, authMethod === "email" && styles.toggleTextActive]}>
                                Email
                            </Text>
                        </TouchableOpacity>
                        <TouchableOpacity
                            style={[styles.toggleButton, authMethod === "phone" && styles.toggleButtonActive]}
                            onPress={() => {
                                setAuthMethod("phone");
                                setCodeSent(false);
                                setCode("");
                            }}
                        >
                            <Text style={[styles.toggleText, authMethod === "phone" && styles.toggleTextActive]}>
                                Phone
                            </Text>
                        </TouchableOpacity>
                    </View>

                    {/* Input Form */}
                    <View style={styles.form}>
                        {!codeSent ? (
                            <>
                                <Text style={styles.label}>
                                    {authMethod === "phone" ? "Phone Number" : "Email Address"}
                                </Text>
                                <TextInput
                                    style={styles.input}
                                    value={authMethod === "phone" ? phoneNumber : email}
                                    onChangeText={authMethod === "phone" ? setPhoneNumber : setEmail}
                                    placeholder={authMethod === "phone" ? "Enter your phone number" : "Enter your email address"}
                                    placeholderTextColor="#999"
                                    keyboardType={authMethod === "phone" ? "phone-pad" : "email-address"}
                                    autoCapitalize="none"
                                />
                                <TouchableOpacity
                                    style={[styles.button, isLoading && styles.buttonDisabled]}
                                    onPress={handleSendCode}
                                    disabled={isLoading}
                                >
                                    <Text style={styles.buttonText}>
                                        {isLoading ? "Sending..." : `Send ${authMethod === "phone" ? "SMS" : "Email"} Code`}
                                    </Text>
                                </TouchableOpacity>
                            </>
                        ) : (
                            <>
                                <Text style={styles.label}>Verification Code</Text>
                                <TextInput
                                    style={styles.input}
                                    value={code}
                                    onChangeText={setCode}
                                    placeholder="Enter 6-digit code"
                                    placeholderTextColor="#999"
                                    keyboardType="numeric"
                                    maxLength={6}
                                />
                                <TouchableOpacity
                                    style={[styles.button, isLoading && styles.buttonDisabled]}
                                    onPress={handleLogin}
                                    disabled={isLoading}
                                >
                                    <Text style={styles.buttonText}>
                                        {isLoading ? "Verifying..." : "Verify & Login"}
                                    </Text>
                                </TouchableOpacity>
                                <TouchableOpacity
                                    style={styles.secondaryButton}
                                    onPress={() => {
                                        setCodeSent(false);
                                        setCode("");
                                    }}
                                >
                                    <Text style={styles.secondaryButtonText}>Back</Text>
                                </TouchableOpacity>
                            </>
                        )}
                    </View>
                </View>
            </View>
        </ImageBackground>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        width: "100%",
        height: "100%",
    },
    overlay: {
        flex: 1,
        backgroundColor: "rgba(249, 245, 243, 0.85)", // F9F5F3 with opacity
        justifyContent: "center",
        alignItems: "center",
        padding: 20,
    },
    content: {
        width: "100%",
        maxWidth: 400,
        backgroundColor: "rgba(255, 255, 255, 0.95)",
        borderRadius: 16,
        padding: 32,
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 4 },
        shadowOpacity: 0.15,
        shadowRadius: 12,
        elevation: 8,
    },
    titleContainer: {
        marginBottom: 32,
        alignItems: "center",
    },
    title: {
        fontSize: 64,
        fontWeight: "700",
        textAlign: "center",
        color: "#000000",
        marginBottom: 8,
        fontFamily: "Arvo_700Bold, serif",
    },
    subtitle: {
        fontSize: 18,
        textAlign: "center",
        color: "rgba(0, 0, 0, 0.8)",
        fontFamily: "AbhayaLibre_400Regular, serif",
    },
    toggleContainer: {
        flexDirection: "row",
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        borderRadius: 8,
        padding: 4,
        marginBottom: 24,
    },
    toggleButton: {
        flex: 1,
        paddingVertical: 12,
        paddingHorizontal: 16,
        borderRadius: 6,
        alignItems: "center",
    },
    toggleButtonActive: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
    },
    toggleText: {
        fontSize: 16,
        fontWeight: "700",
        color: "#000000",
        fontFamily: "AbhayaLibre_700Bold, serif",
    },
    toggleTextActive: {
        color: "#4d2c1d",
    },
    form: {
        width: "100%",
    },
    label: {
        fontSize: 18,
        fontWeight: "700",
        color: "#000000",
        marginBottom: 8,
        fontFamily: "AbhayaLibre_700Bold, serif",
    },
    input: {
        borderWidth: 1,
        borderColor: "rgba(255, 255, 255, 0.3)",
        borderRadius: 8,
        padding: 16,
        fontSize: 18,
        marginBottom: 20,
        backgroundColor: "rgba(255, 255, 255, 0.2)",
        color: "#FFFFFF",
        fontFamily: "AbhayaLibre_400Regular, serif",
    },
    button: {
        backgroundColor: "rgba(255, 255, 255, 0.9)",
        borderRadius: 8,
        paddingVertical: 16,
        paddingHorizontal: 24,
        alignItems: "center",
        marginBottom: 12,
    },
    buttonDisabled: {
        opacity: 0.5,
    },
    buttonText: {
        color: "#4d2c1d",
        fontSize: 20,
        fontWeight: "700",
        fontFamily: "AbhayaLibre_700Bold, serif",
    },
    secondaryButton: {
        backgroundColor: "transparent",
        borderRadius: 8,
        paddingVertical: 12,
        paddingHorizontal: 24,
        alignItems: "center",
        borderWidth: 1,
        borderColor: "rgba(255, 255, 255, 0.4)",
    },
    secondaryButtonText: {
        color: "#000000",
        fontSize: 18,
        fontWeight: "400",
        fontFamily: "AbhayaLibre_400Regular, serif",
    },
    loadingText: {
        fontSize: 18,
        color: "#666",
        fontFamily: "AbhayaLibre_400Regular, serif",
    },
    successText: {
        fontSize: 18,
        color: "#4d2c1d",
        fontWeight: "600",
        fontFamily: "AbhayaLibre_700Bold, serif",
    },
});

export default LoginScreen;

